import * as _ from 'lodash';
import { HlaLocusGroup } from '@nkr/models/hla';
import { IDonorProfileHlaInfoData, IDonorProfileHlaInfoLookupData } from '@nkr/models/donor-profile';
import { Col, ControlLabel, FormControl, FormGroup, HelpBlock, Panel, Row, Table } from 'react-bootstrap';
import { HlaAntigenSelectionForm, HlaDrb345ToggleForm } from '@nkr/components/form/hla';
import { updateProfileHlaInfo } from './update-profile-hla-info';

interface IProps {
    data: IDonorProfileHlaInfoData,
    labReviewInitials: string,
    lookupData: IDonorProfileHlaInfoLookupData,
    missingInitials: boolean,
    invalidReviewDate: boolean,
    isEarlierThanLatestReview: boolean,
    isFutureDate: boolean,
    dirty: boolean,
    onDataChange: (data: IDonorProfileHlaInfoData) => void,
    onInitialsChange: (initials: string) => void
}

const HLAClass1 = (props: IProps) => {
    const aAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'A');
    const bAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'B');
    const bwAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'Bw');
    const cwAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'Cw');
    const alleles = props.data.alleles ?? [];

    const handleNumberChange = (e: React.ChangeEvent<HTMLSelectElement & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = parseInt(e.currentTarget.value);
        let data = _.cloneDeep(props.data);

        data = updateProfileHlaInfo.onNumericChange(name, value, data);

        props.onDataChange(data);
    };

    const handleStringChange = (e: React.ChangeEvent<HTMLSelectElement & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        data = updateProfileHlaInfo.onTextChange(name, value, data);

        props.onDataChange(data);
    };

    const handleStartDateChange = (e: React.ChangeEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        data = updateProfileHlaInfo.onDateChange(name, value, data);

        props.onDataChange(data);
    };


    const handleInitialsChange = (e: React.ChangeEvent<FormControl & HTMLInputElement>) => {
        props.onInitialsChange(e.currentTarget.value);
    };

    return (
        <>
            <Col xs={12} lg={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Panel bsStyle='primary' style={{ borderColor: '#e7f1fa', flex: 1 }}>
                    <Panel.Heading style={{
                        backgroundColor: '#1e3a5f',
                        borderColor: '#1e3a5f',
                        color: '#fff',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        letterSpacing: '1px'
                    }}>
                        HLA Class I
                    </Panel.Heading>
                    <Panel.Body style={{ padding: '6px' }}>

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaA1'}
                    antigen1Value={props.data.hlaA1}
                    antigen2Name={'hlaA2'}
                    antigen2Value={props.data.hlaA2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.A)}
                    antigenOptions={aAntigens.antigens}
                    title={'A'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaB1'}
                    antigen1Value={props.data.hlaB1}
                    antigen2Name={'hlaB2'}
                    antigen2Value={props.data.hlaB2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.B)}
                    antigenOptions={bAntigens.antigens}
                    title={'B'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />
                
                <HlaAntigenSelectionForm
                    antigen1Name={'hlaBw1'}
                    antigen1Value={props.data.hlaBw1}
                    antigen2Name={'hlaBw2'}
                    antigen2Value={props.data.hlaBw2}
                    alleles={[]}
                    antigenOptions={bwAntigens.antigens}
                    title={'Bw'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaC1'}
                    antigen1Value={props.data.hlaC1}
                    antigen2Name={'hlaC2'}
                    antigen2Value={props.data.hlaC2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.C)}
                    antigenOptions={cwAntigens.antigens}
                    title={'Cw'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />

                <div>&nbsp;</div>

                <FormGroup validationState={props.dirty && props.invalidReviewDate ? 'error' : null}>
                    <Col xs={3} lg={2} componentClass={ControlLabel} style={{ textAlign: 'left' }}>Last Reviewed Date:</Col>
                    <Col xs={5} lg={4}>
                        <FormControl
                            type='date'
                            name='labReviewDate'
                            value={props.data.labReviewDate}
                            max={new Date().toISOString().split('T')[0]}   // 👈 today only
                            onChange={handleStartDateChange}
                        />

                        {props.dirty && props.isEarlierThanLatestReview && (
                            <HelpBlock>
                                To continue, review date cannot be earlier than the latest review date...
                            </HelpBlock>
                        )}

                        {props.dirty && props.isFutureDate && (
                            <HelpBlock>
                                Review date cannot be in the future or earlier than the latest review date.
                            </HelpBlock>
                        )}

                    </Col>
                </FormGroup>

                <FormGroup validationState={props.dirty && props.missingInitials ? 'error' : null}>
                    <Col xs={3} lg={2} componentClass={ControlLabel} style={{ textAlign: 'left' }}>Your Initials:</Col>
                    <Col xs={5} lg={4}>
                        <FormControl
                            type='text'
                            name='labReviewInitials'
                            maxLength={3}
                            value={props.labReviewInitials}
                            onChange={handleInitialsChange}
                        />
                        {props.dirty && props.missingInitials && (
                            <HelpBlock>Your initials are required to continue...</HelpBlock>
                        )}
                    </Col>
                </FormGroup>

                {props.data.reviewHistoryList?.length > 0 && (
                    <FormGroup>
                        <Col xs={5} lg={4} componentClass={ControlLabel} style={{ textAlign: 'left' }}>Review History:</Col>
                        <Col xs={7} lg={8}>
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontFamily: "Arial, sans-serif",
                                    border: "1px solid #ccc"
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            backgroundColor: "#999999",
                                            color: "white",
                                            textAlign: "left",
                                            borderBottom: "1px solid #ccc"
                                        }}
                                    >
                                        <th style={{ padding: "6px", width: "200px" }}>Review</th>
                                        <th style={{ padding: "6px" }}>Log Date</th>
                                        <th style={{ padding: "6px" }}>Review Date</th>
                                        <th style={{ padding: "6px" }}>Initials</th>
                                        <th style={{ padding: "6px" }}>User</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {props.data.reviewHistoryList.map((item, i) => {
                                        const rowColor = i % 2 == 0 ? "#eeeeee" : "white";

                                        return (
                                            <tr
                                                key={"review-" + item.id}
                                                style={{
                                                    backgroundColor: rowColor,
                                                    borderBottom: "1px solid #ccc"
                                                }}
                                            >
                                                <td style={{ padding: "6px", fontWeight: "bold" }}>{item.reviewType}</td>
                                                <td style={{ padding: "6px" }}>{item.logDateTime}</td>
                                                <td style={{ padding: "6px" }}>{item.reviewedDateTime}</td>
                                                <td style={{ padding: "6px" }}>{item.reviewedByInitials}</td>
                                                <td style={{ padding: "6px" }}>{item.reviewedByName}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Col>
                    </FormGroup>
                )}
                    </Panel.Body>
                </Panel>
            </Col>
        </>
    )
}

export default HLAClass1;